import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MobileSticky from "../components/MobileSticky";
import QuoteForm from "../components/QuoteForm";
import Seo, { localBusinessSchema, serviceSchema, breadcrumbSchema } from "../components/Seo";
import { COMPANY } from "../data/site";
import { ArrowRight, Phone, Check } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&w=2200&q=85";

const BULLETS = [
  "Full body corporate & strata repaints",
  "Apartment and unit complex exteriors",
  "Interior common areas, lobbies and hallways",
  "Stairwells, walkways and shared amenities",
  "Ongoing maintenance painting programs",
  "Surface preparation and minor patching",
  "Scheduling around residents to minimise disruption",
  "Fixed-price quoting and clear scope documents",
];

const SECTIONS = [
  {
    title: "Repaints that respect residents",
    body:
      "Body corporate painting is different to a standard residential job. Residents live on-site, common areas need to stay accessible, and painting noise and access has to work around real people going about their day. Our body corporate crews are used to working in occupied buildings — we co-ordinate with committees and property managers, communicate a clear schedule, and keep sites tidy every single day.",
  },
  {
    title: "Coastal-grade exterior systems",
    body:
      "Sunshine Coast unit blocks and apartment complexes take a beating from salt air, UV and storm season. We specify UV-stable, mould-resistant exterior paint systems designed for coastal exposure, applied in properly-prepared multi-coat systems. The result: a finish that still looks sharp six to eight years later — even on high-exposure buildings.",
  },
  {
    title: "Common areas, done well",
    body:
      "Lobbies, hallways, stairwells and shared amenities need to look considered and hold up to daily traffic. We use washable acrylic systems for interior common areas, sharp cutting-in at every skirting and cornice, and clean-down at the end of every shift.",
  },
];

export default function BodyCorporate() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = `${origin}/body-corporate-painters-sunshine-coast`;

  return (
    <div data-testid="page-body-corporate">
      <Seo
        title="Body Corporate Painters Sunshine Coast | Connolly's Painting Solutions"
        description="Body corporate & strata painters on the Sunshine Coast. Apartment and unit complex repaints, common areas, stairwells and exteriors. Fixed-price quotes, minimal disruption to residents."
        path="/body-corporate-painters-sunshine-coast"
        image={HERO}
        schema={[
          localBusinessSchema(COMPANY),
          serviceSchema({
            name: "Body Corporate Painters Sunshine Coast",
            description:
              "Body corporate and strata painting for apartment and unit complexes on the Sunshine Coast. Exterior repaints, common areas, stairwells and maintenance painting.",
            url: pageUrl,
          }),
          breadcrumbSchema([
            { name: "Home", url: `${origin}/` },
            { name: "Body Corporate Painters Sunshine Coast", url: pageUrl },
          ]),
        ]}
      />
      <Header />

      {/* HERO */}
      <section className="relative bg-[#0B1426] text-white" style={{ minHeight: "60vh" }}>
        <div className="absolute inset-0">
          <img src={HERO} alt="Body corporate painting on the Sunshine Coast" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1426]/40 via-[#0B1426]/55 to-[#0B1426]" />
        </div>
        <div
          className="relative container-cps max-w-4xl py-24"
          style={{ paddingTop: "clamp(96px, 22vw, 192px)", paddingBottom: "clamp(96px, 18vw, 160px)" }}
        >
          <div className="overline">Sunshine Coast · Strata & Body Corporate</div>
          <h1 className="font-display text-[2.4rem] leading-[1.1] mt-12 md:text-5xl lg:text-6xl md:mt-3 tracking-tight md:leading-[1.05]">
            Body Corporate Painters{" "}<br />on the <span style={{ color: "#C5A059" }}>Sunshine Coast.</span>
          </h1>
          <div className="hairline-gold mt-10 md:hidden" />
          <p className="hidden md:block mt-5 text-lg text-white/75 max-w-2xl leading-relaxed">
            Full-service body corporate and strata painters — apartment complex exteriors, common areas, stairwells and maintenance programs across the Sunshine Coast.
          </p>
          <div className="hidden md:flex mt-7 flex-wrap items-center gap-3" data-testid="bc-hero-ctas">
            <Link to="/quote" className="btn-gold text-base" style={{ padding: "16px 32px" }} data-testid="bc-hero-quote">
              Get a Free Quote <ArrowRight size={16} className="ml-2" />
            </Link>
            <a href={`tel:${COMPANY.phoneTel}`} className="btn-ghost-light" data-testid="bc-hero-call">
              <Phone size={14} className="mr-2" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="bg-[#F9F8F6] py-14 md:py-20">
        <div className="container-cps grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-7">
            <div className="overline">What we do</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight leading-tight text-[#0B1426]">
              Body corporate painting, done properly.
            </h2>
            <p className="mt-5 text-[#4F5B6A] leading-relaxed">
              Connolly's Painting Solutions has been repainting apartment blocks, unit complexes and body corporate common areas across the Sunshine Coast for over 20 years. We work directly with committees, strata managers, building managers and property managers — from single-block walk-ups through to multi-storey coastal high-rises.
            </p>
            <p className="mt-4 text-[#4F5B6A] leading-relaxed">
              Every job starts with an on-site scope — we walk the property, note the substrates, exposure, access points and any prep work needed. You get a clear fixed-price quote with a written scope document you can put in front of your committee before any work begins.
            </p>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {BULLETS.map((b) => (
                <li key={b} className="flex gap-2.5 text-[#0B1426] text-[15px]">
                  <Check size={16} className="text-[#C5A059] flex-shrink-0 mt-1" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10">
              <Link to="/quote" className="btn-gold text-base" style={{ padding: "16px 32px" }} data-testid="bc-mid-quote">
                Request a Body Corporate Quote <ArrowRight size={16} className="ml-2" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden bg-[#EEEAE0]">
              <img
                src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85"
                alt="Apartment complex repaint, Sunshine Coast"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* DEEP CONTENT */}
      <section className="bg-white py-14 md:py-20" data-testid="bc-detail">
        <div className="container-cps grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {SECTIONS.map((s) => (
            <div key={s.title}>
              <div className="hairline-gold w-10 mb-4" />
              <h3 className="font-display text-xl md:text-2xl text-[#0B1426] tracking-tight">{s.title}</h3>
              <p className="mt-3 text-[#4F5B6A] leading-relaxed text-[15px]">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROCESS / FOR COMMITTEES */}
      <section className="bg-[#F9F8F6] py-14 md:py-20" data-testid="bc-committee">
        <div className="container-cps grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline">For committees & managers</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight leading-tight text-[#0B1426]">
              Clear scope. Fixed price. On-time delivery.
            </h2>
            <p className="mt-5 text-[#4F5B6A] leading-relaxed">
              Body corporate committees and property managers deal with enough moving parts already. Our approach removes as many unknowns as possible before work starts.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { t: "On-site scope walk", d: "We physically walk the property and document every element to be painted — no vague inclusions or exclusions." },
              { t: "Fixed-price quote", d: "One clear number, in writing, with a scope your committee can approve without ambiguity." },
              { t: "Scheduled around residents", d: "Access, noisy work and paint times are scheduled to minimise disruption to tenants and owners." },
              { t: "Daily site tidy-up", d: "Drop sheets, masking, clean-down every day. Common areas stay usable throughout the job." },
            ].map((b) => (
              <div key={b.t}>
                <div className="hairline-gold mb-3 w-8" />
                <h3 className="font-display text-lg md:text-xl text-[#0B1426]">{b.t}</h3>
                <p className="mt-2 text-[#4F5B6A] leading-relaxed text-[15px]">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED SERVICES */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-cps">
          <div className="overline">Related services</div>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[15px]">
            <Link
              to="/commercial-painting"
              className="text-[#0B1426] hover:text-[#C5A059] underline decoration-[#C5A059]/40 decoration-1 underline-offset-4"
              data-testid="bc-related-commercial"
            >
              Commercial Painting
            </Link>
            <Link
              to="/exterior-painting"
              className="text-[#0B1426] hover:text-[#C5A059] underline decoration-[#C5A059]/40 decoration-1 underline-offset-4"
              data-testid="bc-related-exterior"
            >
              Exterior Painting
            </Link>
            <Link
              to="/interior-painting"
              className="text-[#0B1426] hover:text-[#C5A059] underline decoration-[#C5A059]/40 decoration-1 underline-offset-4"
              data-testid="bc-related-interior"
            >
              Interior Painting
            </Link>
            <Link
              to="/roof-painting-sunshine-coast"
              className="text-[#0B1426] hover:text-[#C5A059] underline decoration-[#C5A059]/40 decoration-1 underline-offset-4"
              data-testid="bc-related-roof"
            >
              Roof Painting
            </Link>
            <Link
              to="/painters-maroochydore"
              className="text-[#0B1426] hover:text-[#C5A059] underline decoration-[#C5A059]/40 decoration-1 underline-offset-4"
              data-testid="bc-related-maroochydore"
            >
              Painters Maroochydore
            </Link>
            <Link
              to="/painters-mooloolaba"
              className="text-[#0B1426] hover:text-[#C5A059] underline decoration-[#C5A059]/40 decoration-1 underline-offset-4"
              data-testid="bc-related-mooloolaba"
            >
              Painters Mooloolaba
            </Link>
          </div>
        </div>
      </section>

      {/* QUOTE FORM SECTION */}
      <section className="section bg-[#0B1426] text-white" data-testid="bc-quote-section">
        <div className="container-cps">
          <div className="lg:hidden text-center max-w-2xl mx-auto">
            <div className="overline" style={{ color: "rgba(255,255,255,0.7)" }}>Free quote today</div>
            <h2 className="font-display text-3xl mt-3 tracking-tight leading-tight">Request a body corporate quote.</h2>
            <p className="text-white/70 mt-4 text-[15px] leading-relaxed">
              Tell us about the property and we'll respond within one business day with a clear scope and fixed price.
            </p>
            <div className="mt-8 flex flex-col items-stretch gap-3">
              <Link to="/quote" className="btn-gold w-full" data-testid="bc-mobile-quote">
                Get a Free Quote <ArrowRight size={14} className="ml-2" />
              </Link>
              <a href={`tel:${COMPANY.phoneTel}`} className="btn-ghost-light w-full" data-testid="bc-mobile-call">
                Call {COMPANY.phone}
              </a>
            </div>
          </div>

          <div className="hidden lg:grid grid-cols-12 gap-12">
            <div className="col-span-5">
              <div className="overline">Free quote today</div>
              <h2 className="font-display text-5xl mt-4 tracking-tight">Request a body corporate quote.</h2>
              <p className="text-white/70 mt-5 leading-relaxed max-w-md">
                Tell us about the property and we'll respond within one business day with a written scope and fixed price your committee can approve.
              </p>
              <div className="mt-10 space-y-3 text-white/75 text-sm">
                <div>📞 <a href={`tel:${COMPANY.phoneTel}`} className="hover:text-white">{COMPANY.phone}</a></div>
                <div>✉ <a href={`mailto:${COMPANY.email}`} className="hover:text-white">{COMPANY.email}</a></div>
                <div>📍 Servicing the entire Sunshine Coast</div>
              </div>
            </div>
            <div className="col-span-7 bg-white text-[#0B1426] p-12">
              <QuoteForm sourcePage="body-corporate" />
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <MobileSticky />
    </div>
  );
}
